<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import FosButton from '@/components/ui/FosButton.vue'
import { useWizardStore } from '@/stores/wizard'
import { IncidentStatus } from '@/types'

const router = useRouter()
const wizard = useWizardStore()

interface ReviewSection {
  step: number
  title: string
  complete: boolean
  summary: string[]
}

const MISSION_TYPE_LABELS: Record<string, string> = {
  lost_person: 'Lost Person',
  rescue: 'Rescue',
  recovery: 'Recovery',
  other: 'Other',
}

const sections = computed<ReviewSection[]>(() => [
  {
    step: 1,
    title: 'Mission Identity',
    complete: wizard.step1Valid,
    summary: [
      wizard.step1.mission_name || '—',
      wizard.step1.mission_number ? `#${wizard.step1.mission_number}` : 'No mission number',
      wizard.step1.mission_type
        ? (MISSION_TYPE_LABELS[wizard.step1.mission_type] ?? wizard.step1.mission_type)
        : 'Type not set',
      `${wizard.step1.start_date} ${wizard.step1.start_time}`,
      `OP period: ${wizard.step1.op_period_hours}h`,
    ] as string[],
  },
  {
    step: 2,
    title: 'Subject & Situation',
    complete: wizard.step2HasData,
    summary: wizard.step2HasData
      ? ([
          [wizard.step2.subject_first_name, wizard.step2.subject_last_name].filter(Boolean).join(' ') || null,
          wizard.step2.subject_category || null,
          wizard.step2.lkp_lat !== null && wizard.step2.lkp_lng !== null
            ? `LKP: ${wizard.step2.lkp_lat.toFixed(6)}, ${wizard.step2.lkp_lng.toFixed(6)}`
            : null,
          wizard.step2.terrain_types.length
            ? `Terrain: ${wizard.step2.terrain_types.join(', ')}`
            : null,
        ] as Array<string | null>).filter((s): s is string => s !== null)
      : ['No subject data entered'],
  },
  {
    step: 3,
    title: 'Objectives',
    complete: wizard.step3.objectives.length > 0,
    summary:
      wizard.step3.objectives.length > 0
        ? wizard.step3.objectives.slice(0, 3).concat(
            wizard.step3.objectives.length > 3
              ? [`+${wizard.step3.objectives.length - 3} more`]
              : [],
          )
        : ['No objectives added'],
  },
  {
    step: 4,
    title: 'Communications',
    complete: wizard.step4.channels.length > 0,
    summary: wizard.step4.channels
      .map((c) => {
        const freqPart = c.rx_freq || c.tx_freq
          ? ` — ${[c.rx_freq ? `RX ${c.rx_freq}` : '', c.tx_freq ? `TX ${c.tx_freq}` : ''].filter(Boolean).join(' / ')}`
          : c.remarks ? ` (${c.remarks})` : ''
        return `${c.assignment}: ${c.name}${freqPart}`
      })
      .concat(wizard.step4.icp_location ? [`ICP: ${wizard.step4.icp_location}`] : []),
  },
])

async function handleLaunch(status: IncidentStatus.Active | IncidentStatus.Draft) {
  try {
    const incidentId = await wizard.launch(status)
    wizard.reset()
    if (status === IncidentStatus.Active) {
      router.push({ name: 'incident-overview', params: { id: incidentId } })
    } else {
      router.push({ name: 'incident-list' })
    }
  } catch (err) {
    console.error('Failed to launch incident:', err)
  }
}
</script>

<template>
  <div class="flex flex-col gap-6">
    <div>
      <h2 class="font-headline font-black text-lg uppercase tracking-tight text-on-surface">Review & Launch</h2>
      <p class="font-mono text-xs text-outline mt-1">
        Check everything before launching your incident.
      </p>
    </div>

    <!-- Sections -->
    <div class="flex flex-col gap-3">
      <div
        v-for="section in sections"
        :key="section.step"
        class="border border-border overflow-hidden"
      >
        <button
          type="button"
          class="w-full flex items-center justify-between px-4 py-3 bg-surface-container-low hover:bg-surface-container transition-colors text-left"
          @click="wizard.currentStep = section.step"
        >
          <div class="flex items-center gap-3">
            <span class="font-mono text-sm font-bold text-on-surface">
              {{ section.title }}
            </span>
            <span
              class="font-mono text-[0.65rem] font-bold uppercase tracking-widest px-2 py-0.5"
              :class="
                section.complete
                  ? 'bg-surface-container-highest text-green-400'
                  : 'bg-surface-container-highest text-outline'
              "
            >
              {{ section.complete ? 'Complete' : section.step === 1 ? 'Required' : 'Skipped' }}
            </span>
          </div>
          <span class="material-symbols-outlined text-outline text-[18px]">edit</span>
        </button>
        <ul class="px-4 py-3 flex flex-col gap-1">
          <li
            v-for="(line, idx) in section.summary"
            :key="idx"
            class="font-mono text-xs text-on-surface-variant"
          >
            {{ line }}
          </li>
        </ul>
      </div>
    </div>

    <!-- Actions -->
    <div class="flex flex-col gap-3 pt-2">
      <FosButton
        class="w-full"
        :disabled="!wizard.step1Valid || wizard.launching"
        @click="handleLaunch(IncidentStatus.Active)"
      >
        {{ wizard.launching ? 'Launching...' : 'Launch mission' }}
      </FosButton>
      <FosButton
        variant="secondary"
        class="w-full"
        :disabled="!wizard.step1Valid || wizard.launching"
        @click="handleLaunch(IncidentStatus.Draft)"
      >
        {{ wizard.launching ? '...' : 'Save as draft' }}
      </FosButton>
    </div>

    <p class="font-mono text-[0.65rem] text-outline text-center pb-2">
      Launching creates your incident dashboard and ICS forms. You can edit all details after launch.
    </p>

    <!-- Back nav -->
    <div class="flex justify-start">
      <FosButton variant="secondary" size="sm" @click="wizard.currentStep = 4">← Back</FosButton>
    </div>
  </div>
</template>
